/* eslint-disable promise/prefer-await-to-then */
import { Route, Routes } from '@angular/router';
import { MsalRedirectComponent } from '@azure/msal-angular';
import { FeatureId } from '@backModels/FeatureId';
import { PermissionLevelValue } from '@backModels/Ref.PermissionLevelValue';
import { AccessProfileGuard } from '@shared/guards/access-profile.guard';
import { AccessVerifierGuard } from '@shared/guards/access-verifier.guard';
import { InercyaOnlyGuard } from '@shared/guards/inercya-only.guard';
import { SuperAdminGuard } from '@shared/guards/super-admin.guard';
import { CampaignSettingsEntryGuard } from '@shared/guards/campaign-settings-entry.guard';
import { RouteSymbols } from './route-symbols';

const checkupChildrenRoutes: Route[] = [
  {
    path: 'management/form-steps',
    loadComponent: () => import('./management/forms-wiring/forms-wiring.component').then(m => m.FormsWiringComponent),
    canActivate: [InercyaOnlyGuard],
    data: {
      breadcrumb: 'FormStepsManagement',
    },
  },
  {
    path: RouteSymbols.CheckupDetail,
    loadComponent: () => import('./campaigns/campaign/checkups/checkup-detail/checkup-detail.component').then(m => m.CheckupDetailComponent),
    canActivate: [AccessVerifierGuard],
    data: {
      featureId: FeatureId.CheckupDetail,
      permissions: [PermissionLevelValue.Read],
      breadcrumb: 'CheckupDetail',
    },
  },
];

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./home/home.component').then(m => m.HomeComponent),
    canActivate: [AccessProfileGuard],
    data: {
      breadcrumb: 'Home',
    },
  },
  {
    path: 'home-customer',
    loadComponent: () => import('./home/home.component').then(m => m.HomeComponent),
    canActivate: [AccessProfileGuard],
  },
  {
    path: RouteSymbols.CustomerLogin,
    loadComponent: () => import('./customer-login/customer-login.component').then(m => m.CustomerLoginComponent),
    children: [
      {
        path: RouteSymbols.CustomerLoginPinValidation,
        loadComponent: () => import('./customer-login/customer-login-pin-validation/customer-login-pin-validation.component').then(m => m.CustomerLoginPinValidationComponent),
        //canActivate: [AccessVerifierGuard],
        data: {
          permissions: [PermissionLevelValue.Read],
        },
      },
      {
        path: RouteSymbols.CustomerLoginAddresses,
        loadComponent: () => import('./customer-login/customer-login-addresses/customer-login-addresses.component').then(m => m.CustomerLoginAddressesComponent),
        canActivate: [AccessVerifierGuard],
        data: {
          permissions: [PermissionLevelValue.Read],
        },
      },
    ],
  },
  {
    path: RouteSymbols.Logoff,
    loadComponent: () => import('./logoff/logoff.component').then(m => m.LogoffComponent),
  },
  {
    path: RouteSymbols.FormVisualGuide,
    loadComponent: () => import('./shared/components/form-visual-guide/form-visual-guide.component').then(m => m.FormVisualGuideComponent),
    canActivate: [AccessProfileGuard],
  },
  {
    path: RouteSymbols.Campaigns,
    loadComponent: () => import('./campaigns/campaigns.component').then(m => m.CampaignsComponent),
    canActivate: [AccessProfileGuard, AccessVerifierGuard],
    data: {
      featureId: FeatureId.Campaigns,
      permissions: [PermissionLevelValue.Read],
      breadcrumb: 'Campaigns',
    },
    children: [
      {
        path: RouteSymbols.Campaign,
        loadComponent: () => import('./campaigns/campaign/campaign.component').then(m => m.CampaignComponent),
        canActivate: [AccessVerifierGuard],
        data: {
          featureId: FeatureId.Campaigns,
          permissions: [PermissionLevelValue.Read],
          breadcrumb: '{dynamic[0]}',
        },
        children: [
          {
            path: RouteSymbols.Dashboard,
            loadComponent: () => import('./shared/components/dashboard/dashboard.component').then(m => m.DashboardComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Dashboard,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Dashboard',
            },
          },
          {
            // MUST stay above RouteSymbols.Checkups: that route's :campaignInstanceStatusId would
            // otherwise swallow the literal 'open' segment. Resolves the real status of a checkup
            // for links stored outside the app (emails) and replaces itself with the canonical URL.
            path: RouteSymbols.CheckupOpen,
            loadComponent: () => import('./campaigns/campaign/checkups/checkup-open/checkup-open.component').then(m => m.CheckupOpenComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.CheckupDetail,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'CheckupDetail',
            },
          },
          {
            path: RouteSymbols.Checkups,
            loadComponent: () => import('./campaigns/campaign/checkups/checkups.component').then(m => m.CheckupsComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Checkups,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: '{dynamic[1]}',
            },
            children: checkupChildrenRoutes,
          },
          {
            path: RouteSymbols.CheckupsAll,
            loadComponent: () => import('./campaigns/campaign/checkups/checkups.component').then(m => m.CheckupsComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Checkups,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'All',
            },
            children: checkupChildrenRoutes,
          },
          {
            path: RouteSymbols.Settings,
            loadComponent: () => import('./campaigns/campaign/settings/settings.component').then(m => m.SettingsComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.CampaignSettings,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Setting',
            },
            children: [
              {
                // Lands on the first settings tab the user may open (roles without general data
                // would otherwise hit a 403). Redirect functions run before parent guards load the
                // user, so this is a guard returning a UrlTree instead of a redirectTo. The empty
                // children array is what makes a component-less route valid for the router.
                path: '',
                pathMatch: 'full',
                canActivate: [CampaignSettingsEntryGuard],
                children: [],
              },
              {
                path: RouteSymbols.SettingsGeneralData,
                loadComponent: () => import('./campaigns/campaign/settings/settings-general-data/settings-general-data.component').then(m => m.SettingsGeneralDataComponent),
                canActivate: [AccessVerifierGuard],
                data: {
                  featureId: FeatureId.CampaignSettingsGeneralData,
                  permissions: [PermissionLevelValue.Read],
                  breadcrumb: 'GeneralData',
                },
              },
              {
                path: RouteSymbols.SettingsConfiguration,
                loadComponent: () => import('./campaigns/campaign/settings/settings-configuration/settings-configuration.component').then(m => m.SettingsConfigurationComponent),
                canActivate: [AccessVerifierGuard],
                data: {
                  featureId: FeatureId.CampaignSettingsGeneralData,
                  permissions: [PermissionLevelValue.Read],
                  breadcrumb: 'Settings',
                },
              },
              {
                path: RouteSymbols.SettingsStyles,
                loadComponent: () => import('./campaigns/campaign/settings/settings-styles/settings-styles.component').then(m => m.SettingsStylesComponent),
                canActivate: [AccessVerifierGuard],
                data: {
                  featureId: FeatureId.CampaignSettingsStyles,
                  permissions: [PermissionLevelValue.Read],
                  breadcrumb: 'Styles',
                },
              },
              {
                path: RouteSymbols.SettingsCheckupConfig,
                loadComponent: () => import('./campaigns/campaign/settings/settings-checkup-config/settings-checkup-config.component').then(m => m.SettingsCheckupConfigComponent),
                canActivate: [AccessVerifierGuard],
                data: {
                  featureId: FeatureId.CampaignSettingsCheckupConfig,
                  permissions: [PermissionLevelValue.Read],
                  breadcrumb: 'CheckupConfig',
                },
              },
              {
                path: RouteSymbols.SettingsCustomers,
                loadComponent: () => import('./campaigns/campaign/settings/settings-customers/settings-customers.component').then(m => m.SettingsCustomersComponent),
                canActivate: [AccessVerifierGuard],
                data: {
                  featureId: FeatureId.CampaignSettingsCustomers,
                  permissions: [PermissionLevelValue.Read],
                  breadcrumb: 'Customers',
                },
              },
              {
                path: RouteSymbols.Users,
                loadComponent: () => import('./campaigns/campaign/settings/settings-users/settings-users.component').then(m => m.SettingsUsersComponent),
                canActivate: [AccessVerifierGuard],
                data: {
                  featureId: FeatureId.CampaignSettingsUsers,
                  permissions: [PermissionLevelValue.Read],
                  breadcrumb: 'Users',
                },
              },
              {
                path: RouteSymbols.SettingsHistory,
                loadComponent: () => import('./campaigns/campaign/settings/settings-history/settings-history.component').then(m => m.SettingsHistoryComponent),
                canActivate: [AccessVerifierGuard],
                data: {
                  featureId: FeatureId.CampaignSettingsHistory,
                  permissions: [PermissionLevelValue.Read],
                  breadcrumb: 'History',
                },
              },
              {
                path: RouteSymbols.SettingsEmails,
                loadComponent: () => import('./campaigns/campaign/settings/settings-emails/settings-emails.component').then(m => m.SettingsEmailsComponent),
                canActivate: [AccessVerifierGuard],
                data: {
                  featureId: FeatureId.CampaignSettingsEmails,
                  permissions: [PermissionLevelValue.Read],
                  breadcrumb: 'Emails',
                },
              },
              {
                path: RouteSymbols.SettingsCustomerFeedbacks,
                loadComponent: () => import('./campaigns/campaign/settings/settings-customer-feedbacks/settings-customer-feedbacks.component').then(m => m.SettingsCustomerFeedbacksComponent),
                canActivate: [AccessVerifierGuard],
                data: {
                  featureId: FeatureId.CustomerFeedbacks,
                  permissions: [PermissionLevelValue.Read],
                  breadcrumb: 'Feedbacks',
                },
              },
              {
                path: RouteSymbols.SettingsEnabledZones,
                loadComponent: () => import('./campaigns/campaign/settings/settings-enabled-zones/settings-enabled-zones.component').then(m => m.SettingsEnabledZonesComponent),
                canActivate: [AccessVerifierGuard],
                data: {
                  featureId: FeatureId.CampaignSettingsEnabledZones,
                  permissions: [PermissionLevelValue.Read],
                  breadcrumb: 'AllowedProvinces',
                },
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: RouteSymbols.Companies,
    loadComponent: () => import('./companies/companies.component').then(m => m.CompaniesComponent),
    canActivate: [AccessProfileGuard, AccessVerifierGuard],
    data: {
      featureId: FeatureId.Companies,
      permissions: [PermissionLevelValue.Read],
      breadcrumb: 'Companies',
    },
    children: [
      {
        path: RouteSymbols.Company,
        loadComponent: () => import('./companies/company/company.component').then(m => m.CompanyComponent),
        canActivate: [AccessVerifierGuard],
        data: {
          featureId: FeatureId.Companies,
          permissions: [PermissionLevelValue.Read],
          breadcrumb: '{dynamic[0]}',
        },
        children: [
          {
            path: '',
            redirectTo: RouteSymbols.CompanyDashboard,
            pathMatch: 'full',
          },
          {
            path: RouteSymbols.CompanyDashboard,
            loadComponent: () => import('./companies/company/dashboard/dashboard.component').then(m => m.CompanyDashboardComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Companies,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Dashboard',
            },
          },
          {
            path: RouteSymbols.CompanyCustomers,
            loadComponent: () => import('./companies/company/customers/company-customers.component').then(m => m.CompanyCustomersComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Companies,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Customers',
            },
          },
          {
            path: RouteSymbols.CompanyProducts,
            loadComponent: () => import('./companies/company/products/company-products.component').then(m => m.CompanyProductsComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Companies,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Products',
            },
          },
          {
            path: RouteSymbols.CompanyFinancing,
            loadComponent: () => import('./companies/company/financing/company-financing.component').then(m => m.CompanyFinancingComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Companies,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Financing',
            },
          },
          {
            path: RouteSymbols.CompanyDocuments,
            loadComponent: () => import('./companies/company/documents/company-documents.component').then(m => m.CompanyDocumentsComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Companies,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Documents',
            },
          },
          {
            path: RouteSymbols.CompanyStyles,
            loadComponent: () => import('./companies/company/styles/company-styles.component').then(m => m.CompanyStylesComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Companies,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Styles',
            },
          },
          {
            path: RouteSymbols.CompanyHistory,
            loadComponent: () => import('./companies/company/history/company-history.component').then(m => m.CompanyHistoryComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Companies,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'History',
            },
          },
          {
            path: RouteSymbols.CompanyCustomerFeedbacks,
            loadComponent: () => import('./companies/company/customer-feedbacks/company-customer-feedbacks.component').then(m => m.CompanyCustomerFeedbacksComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.CustomerFeedbacks,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Feedbacks',
            },
          },
          {
            path: RouteSymbols.CompanyEmails,
            loadComponent: () => import('./companies/company/emails/company-emails.component').then(m => m.CompanyEmailsComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Companies,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Emails',
            },
          },
        ],
      },
    ],
  },
  {
    path: RouteSymbols.ValidationComponents,
    loadComponent: () => import('./campaigns/campaign/checkups/checkup-detail/checkup-detail.component').then(m => m.CheckupDetailComponent),
  },
  {
    path: RouteSymbols.Admin,
    loadComponent: () => import('./admin/admin.component').then(m => m.AdminComponent),
    canActivate: [AccessProfileGuard, AccessVerifierGuard],
    data: {
      featureId: FeatureId.Admin,
      permissions: [PermissionLevelValue.Read],
      breadcrumb: 'Admin',
    },
    children: [
      {
        path: RouteSymbols.Security,
        loadComponent: () => import('./admin/security/security.component').then(m => m.SecurityComponent),
        canActivate: [AccessVerifierGuard],
        data: {
          featureId: FeatureId.Security,
          permissions: [PermissionLevelValue.Read],
          breadcrumb: 'Security',
        },
        children: [
          {
            path: '',
            redirectTo: RouteSymbols.Users,
            pathMatch: 'full',
          },
          {
            path: RouteSymbols.Organizations,
            loadComponent: () => import('./admin/security/organizations/organizations.component').then(m => m.OrganizationsComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Organizations,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Organizations',
            },
          },
          {
            path: RouteSymbols.Positions,
            loadComponent: () => import('./admin/security/positions/positions-table/positions-table.component').then(m => m.PositionsTableComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Positions,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Positions',
            },
          },
          {
            path: RouteSymbols.Users,
            loadComponent: () => import('./admin/security/users/users-table/users-table.component').then(m => m.UsersTableComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Users,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'Users',
            },
          },
          {
            path: RouteSymbols.Features,
            loadComponent: () => import('./admin/security/features/features.component').then(m => m.FeaturesComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.Features,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'ModulesOrFeatures',
            },
          },
        ],
      },
      {
        path: RouteSymbols.DataMaintenance,
        canActivate: [AccessVerifierGuard],
        data: {
          featureId: FeatureId.DataMaintenance,
          permissions: [PermissionLevelValue.Read],
          breadcrumb: 'DataMaintenance',
        },
        children: [
          {
            path: '',
            redirectTo: RouteSymbols.MasterDataTypes,
            pathMatch: 'full',
          },
          {
            path: RouteSymbols.MasterDataTypes,
            loadComponent: () => import('./admin/data-maintenance/master-data-item-types/master-data-item-types.component').then(m => m.MasterDataItemTypesComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.MasterDataTypes,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'MasterDataTypes',
            },
          },
          {
            path: RouteSymbols.MasterDataItems,
            loadComponent: () => import('./admin/data-maintenance/master-data-items/master-data-items.component').then(m => m.MasterDataItemsComponent),
            canActivate: [AccessVerifierGuard],
            data: {
              featureId: FeatureId.MasterDataItems,
              permissions: [PermissionLevelValue.Read],
              breadcrumb: 'MasterDataItems',
            },
          },
          {
            path: 'forms-config',
            loadComponent: () => import('./management/forms-wiring/forms-wiring.component').then(m => m.FormsWiringComponent),
            canActivate: [AccessVerifierGuard, InercyaOnlyGuard],
            data: {
              breadcrumb: 'FormsConfig',
            },
          },
        ],
      },
      {
        path: RouteSymbols.Translations,
        loadComponent: () => import('./admin/translations/translations.component').then(m => m.TranslationsComponent),
        canActivate: [AccessProfileGuard, AccessVerifierGuard],
        data: {
          featureId: FeatureId.Translations,
          permissions: [PermissionLevelValue.Read],
          breadcrumb: 'Translations',
        },
      },
      {
        path: RouteSymbols.UserTrackings,
        loadComponent: () => import('./admin/user-trackings/user-trackings.component').then(m => m.UserTrackingsComponent),
        canActivate: [AccessProfileGuard, AccessVerifierGuard],
        data: {
          featureId: FeatureId.UserTrackings,
          permissions: [PermissionLevelValue.Read],
          breadcrumb: 'History',
        },
      },
      {
        path: RouteSymbols.Emails,
        loadComponent: () => import('./admin/emails/admin-emails.component').then(m => m.AdminEmailsComponent),
        canActivate: [AccessProfileGuard, AccessVerifierGuard],
        data: {
          featureId: FeatureId.Emails,
          permissions: [PermissionLevelValue.Read],
          breadcrumb: 'Emails',
        },
      },
      {
        path: RouteSymbols.CustomerFeedbacks,
        loadComponent: () => import('./admin/customer-feedbacks/admin-customer-feedbacks.component').then(m => m.AdminCustomerFeedbacksComponent),
        canActivate: [AccessProfileGuard, AccessVerifierGuard],
        data: {
          featureId: FeatureId.CustomerFeedbacks,
          permissions: [PermissionLevelValue.Read],
          breadcrumb: 'Feedbacks',
        },
      },
      {
        path: RouteSymbols.ScheduledTasks,
        loadComponent: () => import('./admin/scheduled-tasks/scheduled-tasks.component').then(m => m.ScheduledTasksComponent),
        canActivate: [AccessProfileGuard, AccessVerifierGuard],
        data: {
          featureId: FeatureId.ScheduledTasks,
          permissions: [PermissionLevelValue.Read],
          breadcrumb: 'ScheduledTasks',
        },
      },
      {
        path: RouteSymbols.Versions,
        loadComponent: () => import('./admin/versions/versions.component').then(m => m.VersionsComponent),
        canActivate: [AccessVerifierGuard, SuperAdminGuard],
        data: {
          featureId: FeatureId.Admin,
          permissions: [PermissionLevelValue.Read],
          breadcrumb: 'Versions',
        },
      },
    ],
  },
  // Customer feedback page — guard-free public route: the signed link token (?token=) is the credential.
  {
    path: RouteSymbols.CustomerFeedbackPage,
    loadComponent: () => import('./customer-feedback/customer-feedback-page.component').then(m => m.CustomerFeedbackPageComponent),
  },
  // Solar embed — guard-free public route for anonymous visitors.
  // No canActivate. Lazy-loaded so Leaflet/Turf stay out of the main bundle.
  // Follows the same pattern as customer-login (no MSAL guard).
  {
    path: 'company/:companyGuid/:campaignGuid',
    loadChildren: () => import('./solar-embed/solar-embed.routes').then(m => m.SOLAR_EMBED_ROUTES),
  },
  { path: RouteSymbols.Redirect, component: MsalRedirectComponent },
  { path: RouteSymbols.ErrorHTTPCode, loadComponent: () => import('./error/error.component').then(m => m.ErrorComponent) },
  { path: RouteSymbols.NotFound, redirectTo: RouteSymbols.Error404 },
];
